import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { BandMenu, AskBall } from "./BandMenu";
import { PhotoViewer } from "./PhotoViewer";
import { BackupMenu } from "./BackupMenu";
import {
  store, YARN_WEIGHTS, weightLabel, COMMON_FIBERS, COMMON_CURRENCIES,
  todayLocal, fmtYds, usableYardage, isPartial,
  type Skein, type NewSkein, type Pattern, type NewPattern, type Project, type NewProject,
  type PatternStatus, type StashSignals, type PatternFit, type Counter,
} from "./store";
import { recapStash, openWebLogin, assertAllowed, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, Lock, WifiOff, RefreshCw, Layers, ListChecks, BarChart3,
  Plus, AlertTriangle, CheckCircle2, Minus, RotateCcw, Hash,
} from "lucide-react";

const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "";

// SCREENSHOT=0 — sim-only auth bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-skein://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window navigated to oriora-skein://deleted.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Skein"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ fontSize: "2.4rem", marginBottom: "0.4rem" }}>🧶</div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Skein</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Your yarn, ready to cast on</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your stash lives on this
          device; the stash recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try { const p = platform(); if (p === "ios") await signInIos("oriora-skein"); else if (p === "macos") await signInMac("oriora-skein"); else if (p === "web") window.location.href = "/login?next=/oriora-skein/"; else await openWebLogin(); }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [skeins, setSkeins] = useState<Skein[]>([]);
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // The billed recap is folded onto the cast-on surface as an action (not a peer tab).
  const [recapOpen, setRecapOpen] = useState(false);
  // The stash is reachable FROM a cast-on card ("you're 2 skeins short — see stash").
  const stashRef = useRef<HTMLDivElement | null>(null);
  const seeStash = useCallback(() => stashRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), []);

  // editors
  const [editSkein, setEditSkein] = useState<Skein | "new" | null>(null);
  const [editPattern, setEditPattern] = useState<Pattern | "new" | null>(null);
  const [editProject, setEditProject] = useState<Project | "new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ kind: "skein" | "pattern" | "project"; id: string; label: string } | null>(null);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not
  // change any existing screen or logic; it only drives the store the manual UI already drives.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  // The stash↔queue MATCH is the product. `compute_signals` is a local Rust call (no network, no
  // billing), so the home screen can lead with the answer instead of hiding it in the Recap tab.
  const [homeSignals, setHomeSignals] = useState<StashSignals | null>(null);
  const reload = useCallback(async () => {
    const [s, p, pr] = await Promise.all([store.listSkeins(), store.listPatterns(), store.listProjects()]);
    setSkeins(s); setPatterns(p); setProjects(pr);
    try { setHomeSignals(await store.computeSignals(todayLocal())); }
    catch { setHomeSignals(null); }   // a signals hiccup must never blank the stash
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const exportRef = useRef<(() => void) | null>(null);
  const doExport = useCallback(() => {
    if (skeins.length === 0) return;
    const { csv, filename } = buildExport(skeins);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [skeins]);
  exportRef.current = doExport;

  // Native-menu actions (macOS Actions menu): Add yarn / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add_skein": setEditSkein("new"); break;
        case "recap": setRecapOpen(true); break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const totalUsable = useMemo(() => skeins.reduce((a, s) => a + usableYardage(s), 0), [skeins]);
  const queuedCount = useMemo(() => patterns.filter((p) => p.status === "queued" || p.status === "making").length, [patterns]);

  async function doDelete() {
    if (!confirmDelete) return;
    const { kind, id } = confirmDelete;
    setConfirmDelete(null);
    if (kind === "skein") await store.deleteSkein(id);
    else if (kind === "pattern") await store.deletePattern(id);
    else await store.deleteProject(id);
    await reload();
  }

  return (
    <main className="sk-app">
      {offline && (
        <div role="status" className="row sk-offline">
          <WifiOff size={14} style={{ flexShrink: 0 }} /> Stash recap paused — your stash still saves locally
        </div>
      )}

      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The CAST-ON VERDICT is the
          header — this app's whole promise is "can I cast this on?", so the answer leads. The two
          round menu buttons collapsed into ONE skein-band control (BandMenu). Do not reintroduce
          a wordmark strip, and do not split the control back into two circles. */}
      <header className="sk-header">
        <BandMenu
          backup={
            <BackupMenu
              appName="Skein" fileSlug="skein" appScheme="oriora-skein"
              hasData={skeins.length > 0 || patterns.length > 0 || projects.length > 0} onRestored={reload}
            />
          }
          account={<AccountMenu email={email} appSlug="oriora-skein" appName="Oriora Skein" onSignedOut={onSignedOut} />}
        />
      </header>

      {/* THE CAST-ON SURFACE — the app opens on the stash↔queue verdict ("can I cast this on?"),
          on its OWN surface, not the shared `.hero` band the fleet siblings sit in. The three-tab
          pill row is gone; the billed recap is folded here as a knitting-language action. */}
      <CastOnSurface
        signals={homeSignals} queued={queuedCount}
        onAddPattern={() => setEditPattern("new")}
        recapOpen={recapOpen} onToggleRecap={() => setRecapOpen((o) => !o)}
        onSeeStash={seeStash}
        firstRun={skeins.length === 0 && patterns.length === 0}
        onSaved={reload}
      />

      {recapOpen && (
        <div className="sk-recap-slot">
          <RecapTab skeinsCount={skeins.length} patternsCount={patterns.length} />
        </div>
      )}

      {/* The queue as a set of cast-on cards, each carrying its own fit verdict. */}
      <PatternQueueSection
        onAsk={() => setChatOpen(true)}
        patterns={patterns} fits={homeSignals?.pattern_fit ?? []}
        firstRun={skeins.length === 0 && patterns.length === 0}
        onAddPattern={() => setEditPattern("new")}
        onEditPattern={(p) => setEditPattern(p)}
        onDeletePattern={(p) => setConfirmDelete({ kind: "pattern", id: p.id, label: p.name || "this pattern" })}
        onSeeStash={seeStash}
      />

      {/* Row counters — the knitting table-stake, free + on-device. */}
      <CountersSection />

      {/* The stash wall — a scroll below the verdict, reachable from a card's shortfall. */}
      <div ref={stashRef}>
        <StashSection
          skeins={skeins} totalUsable={totalUsable}
          onAdd={() => setEditSkein("new")}
          onEdit={(s) => setEditSkein(s)}
          onDelete={(s) => setConfirmDelete({ kind: "skein", id: s.id, label: `${s.brand} ${s.colorway}`.trim() || "this yarn" })}
          onExport={doExport}
        />
      </div>

      {/* Finished projects — a separate object with its own life-cycle (split out of the queue). */}
      <FinishedProjectsSection
        projects={projects}
        onAddProject={() => setEditProject("new")}
        onEditProject={(p) => setEditProject(p)}
        onDeleteProject={(p) => setConfirmDelete({ kind: "project", id: p.id, label: p.name || "this project" })}
      />

      {editSkein && (
        <SkeinEditor
          editing={editSkein === "new" ? null : editSkein}
          onClose={() => setEditSkein(null)}
          onSaved={() => { setEditSkein(null); void reload(); }}
        />
      )}
      {editPattern && (
        <PatternEditor
          editing={editPattern === "new" ? null : editPattern}
          onClose={() => setEditPattern(null)}
          onSaved={() => { setEditPattern(null); void reload(); }}
        />
      )}
      {editProject && (
        <ProjectEditor
          editing={editProject === "new" ? null : editProject}
          onClose={() => setEditProject(null)}
          onSaved={() => { setEditProject(null); void reload(); }}
        />
      )}

      {confirmDelete && (
        <ModalShell title="Delete?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
            Delete {confirmDelete.label}? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={doDelete}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          opens a slide-over that drives the SAME store, then refreshes the list via reload(). */}
      {/* 🔴 IDENTITY (2026-08-30): the floating "Chat" pill that used to launch this was DELETED —
        same pill, same corner, same label in ~15 sibling apps. The assistant is NOT removed and
        must never be: its entry is the .sk-ball yarn ball on the queue rail, which calls this
        same setChatOpen. If a future change moves it, REPLACE it — never drop it. */}
      <aside className={`skc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="skc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, find, or recap</span>
          <button className="skc-close" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// ── STASH — the catalog of owned yarn as a wall of swatches (kept: yarn is colour-first and
//    physical, so it's a wall you scan, not a row list). The warehouse total lives here now,
//    moved off the cast-on surface — a count belongs with the stash, not in the verdict. ───────
// ── Row counters — THE knitting/crochet table-stake (row counter apps are a huge category);
//    free + on-device here, no subscription. Standalone, tap to count while you knit. ──
function CountersSection() {
  const [counters, setCounters] = useState<Counter[]>([]);
  const load = useCallback(async () => { try { setCounters(await store.listCounters()); } catch { setCounters([]); } }, []);
  useEffect(() => { void load(); }, [load]);

  const add = async () => { try { await store.createCounter({ name: "Rows", step: 1 }); await load(); } catch { /* ignore */ } };
  const bump = async (c: Counter, dir: 1 | -1) => {
    try { await store.setCounterCount(c.id, Math.max(0, c.count + dir * c.step)); await load(); } catch { /* ignore */ }
  };
  const reset = async (c: Counter) => { try { await store.setCounterCount(c.id, 0); await load(); } catch { /* ignore */ } };
  const rename = async (c: Counter) => {
    const name = window.prompt("Counter name", c.name); if (name === null) return;
    const stepStr = window.prompt("Count by (step)", String(c.step)); const step = stepStr ? Number(stepStr) : c.step;
    try { await store.updateCounter(c.id, name, Number.isFinite(step) && step !== 0 ? step : undefined); await load(); } catch { /* ignore */ }
  };
  const remove = async (c: Counter) => { try { await store.deleteCounter(c.id); await load(); } catch { /* ignore */ } };

  return (
    <section style={{ marginTop: "1.5rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
        <h2 className="row" style={{ fontSize: "1.05rem", margin: 0, gap: "0.4rem" }}><Hash size={16} style={{ color: "var(--accent)" }} /> Row counters</h2>
        <button className="btn btn-sm btn-pill" onClick={() => void add()}><Plus size={14} /> New counter</button>
      </div>
      {/* READY-STATE (2026-09-19): this is the category's core tool (PARITY-MATRIX row 1) and it
          is free here — so at zero it is a counter you can TAP, not a paragraph describing one.
          One tap creates it and you are counting; the row that follows is the real component. */}
      {counters.length === 0 ? (
        <button type="button" className="sk-counter0" onClick={() => void add()}>
          <span className="sk-counter0-n tnum">0</span>
          <span className="sk-counter0-txt">
            <span className="sk-counter0-lbl">Tap to start counting</span>
            <span className="sk-counter0-sub">A row counter, free and on this device — no subscription, no sign-up beyond the one you already have.</span>
          </span>
        </button>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem" }}>
          {counters.map((c) => (
            <div key={c.id} className="card" style={{ padding: "0.9rem 1rem", textAlign: "center" }}>
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "0.3rem" }}>
                <button className="link" style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }} onClick={() => void rename(c)}>{c.name}</button>
                <div className="row" style={{ gap: "0.3rem" }}>
                  <button className="btn-ghost" style={{ padding: "0.2rem" }} title="Reset" onClick={() => void reset(c)}><RotateCcw size={13} /></button>
                  <button className="btn-ghost" style={{ padding: "0.2rem", color: "var(--rose)" }} title="Delete" onClick={() => void remove(c)}><Trash2 size={13} /></button>
                </div>
              </div>
              <div className="tnum" style={{ fontSize: "2.6rem", fontWeight: 800, lineHeight: 1.1 }}>{c.count}</div>
              {c.step !== 1 && <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>+{c.step} per tap</div>}
              <div className="row" style={{ gap: "0.5rem", justifyContent: "center", marginTop: "0.6rem" }}>
                <button className="btn btn-pill" style={{ width: 48, height: 44 }} aria-label="Minus" onClick={() => void bump(c, -1)}><Minus size={18} /></button>
                <button className="btn btn-primary btn-pill" style={{ flex: 1, height: 44 }} aria-label="Plus" onClick={() => void bump(c, 1)}><Plus size={20} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StashSection({ skeins, totalUsable, onAdd, onEdit, onDelete, onExport }: {
  skeins: Skein[]; totalUsable: number; onAdd: () => void; onEdit: (s: Skein) => void; onDelete: (s: Skein) => void; onExport: () => void;
}) {
  return (
    <section className="sk-section">
      <div className="sk-section-head">
        <h2 className="sk-section-title">Your stash</h2>
        <div className="row" style={{ gap: "0.5rem" }}>
          {skeins.length > 0 && <span className="sk-section-meta tnum">{skeins.length} {skeins.length === 1 ? "line" : "lines"} · {fmtYds(totalUsable)} yds</span>}
          <button className="btn btn-sm btn-pill" onClick={onAdd}><Plus size={14} /> Add yarn</button>
        </div>
      </div>

      {skeins.length === 0 ? (
        <div className="card" style={{ padding: "2.5rem 1.5rem", textAlign: "center" }}>
          <div style={{ fontSize: "3rem" }}>🧶</div>
          <div className="serif" style={{ fontSize: "1.2rem", margin: "0.5rem 0 0.25rem" }}>Add your first yarn</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Brand, colorway, yardage, dye-lot — your stash, catalogued.</div>
          <button className="btn btn-primary" style={{ marginTop: "1rem" }} onClick={onAdd}>Add yarn</button>
        </div>
      ) : (
        <>
          <div className="sk-wall">
            {skeins.map((s) => <SkeinTile key={s.id} s={s} onEdit={() => onEdit(s)} onDelete={() => onDelete(s)} />)}
          </div>
          <div className="row" style={{ justifyContent: "center", marginTop: "1rem", gap: "0.7rem" }}>
            <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.25rem" }}><Lock size={11} /> on this device</span>
            <button className="btn btn-sm" onClick={onExport}>Export CSV</button>
          </div>
        </>
      )}
    </section>
  );
}

// The swatch face is tinted by YARN WEIGHT — lace (finest) through jumbo (thickest) run light to
// deep. This is derived from real data the user entered, and it is how knitters group a stash in
// the basket: you reach for "a worsted" before you reach for a particular colourway. It is NOT a
// guess at the yarn's actual colour — we have no colour field, and inventing one would put a
// fabricated colour on screen next to the user's own record.
const WEIGHT_TINT: Record<string, string> = {
  "lace":        "#fdf2f5",
  "fingering":   "#fbe2ea",
  "sport":       "#f8cfdc",
  "dk":          "#f4b6cb",
  "worsted":     "#ee94b3",
  "aran":        "#e4739c",
  "bulky":       "#d4557f",
  "super-bulky": "#b53e65",
  "jumbo":       "#8f2f4e",
};
/** Deep tints need light ink on top; the pale end keeps the accent ink. */
const WEIGHT_INK = (w: string) =>
  ["aran", "bulky", "super-bulky", "jumbo"].includes(w) ? "#fff" : "var(--accent)";

// One swatch on the stash wall: the yarn's own photo (or the CYC weight number, which is the 0–7
// symbol every knitter reads) fills the cell face, the remaining amount reads as a level along the
// bottom, and the COLOURWAY leads the label — that is how a knitter names a yarn in their head.
function SkeinTile({ s, onEdit, onDelete }: { s: Skein; onEdit: () => void; onDelete: () => void }) {
  const usable = usableYardage(s);
  const partial = isPartial(s);
  const [zoom, setZoom] = useState(false);
  const wNum = (YARN_WEIGHTS as readonly string[]).indexOf(s.weight);
  const full = s.yardage_per_skein * (s.skein_count || 1);
  const pct = full > 0 ? Math.max(4, Math.min(100, (usable / full) * 100)) : 0;

  return (
    <div className="sk-swatch" onClick={onEdit}>
      {/* The face of the cell IS the yarn. With no photo we show the CYC weight number rather
          than inventing a colour — we have no colour field and guessing one from the colourway
          name would be a fabrication on screen. */}
      <div className="sk-swatch-face">
        {s.photo ? (
          <img src={s.photo} alt={`Photo of ${s.brand || "yarn"}`} title="Tap to view"
            onClick={(ev) => { ev.stopPropagation(); setZoom(true); }} className="sk-swatch-img" />
        ) : (
          <div className="sk-swatch-ph" title={s.weight ? weightLabel(s.weight) : "yarn"}
            style={{ background: WEIGHT_TINT[s.weight] ?? "var(--accent-soft)", color: WEIGHT_INK(s.weight) }}>
            {wNum >= 0
              ? <><span className="tnum sk-swatch-w">{wNum}</span><span className="sk-swatch-wl">{weightLabel(s.weight)}</span></>
              : <span style={{ fontSize: 26 }}>🧶</span>}
          </div>
        )}
        {/* How much is left, read as a level on the cell — the basket-glance question. */}
        {full > 0 && (
          <div className="sk-swatch-level" title={`${Math.round(pct)}% of the full amount remaining`}>
            <div style={{ width: `${pct}%`, background: partial ? "color-mix(in srgb, var(--accent) 65%, #f59e0b)" : "var(--accent)" }} />
          </div>
        )}
        <button className="sk-swatch-del" title="Delete" aria-label="Delete yarn"
          onClick={(ev) => { ev.stopPropagation(); onDelete(); }}><Trash2 size={13} /></button>
      </div>

      <div className="sk-swatch-body">
        {/* Colourway first — it is how a knitter names the yarn in their head. */}
        <div className="sk-swatch-cw">{s.colorway || s.brand || "Unnamed"}</div>
        <div className="sk-swatch-sub">{[s.brand && s.colorway ? s.brand : "", s.fiber].filter(Boolean).join(" · ")}</div>
        <div className="sk-swatch-yds tnum"><strong>{fmtYds(usable)}</strong> yds</div>
        <div className="sk-swatch-chips">
          {partial && <span className="chip sk-mini">partial</span>}
          {s.dye_lot && <span className="chip sk-mini">lot {s.dye_lot}</span>}
        </div>
      </div>

      {zoom && s.photo && (
        <PhotoViewer photos={[{ src: s.photo, alt: `Photo of ${s.brand || "yarn"}` }]} onClose={() => setZoom(false)} />
      )}
    </div>
  );
}

// ── PATTERN QUEUE — the cast-on cards. Each queued pattern carries its own fit verdict; this is
//    the object a knitter actually queues. Finished projects are a SEPARATE section (they were
//    crammed into one "Queue" tab only because the template gave one tab per plural noun). ─────
function PatternQueueSection({ patterns, fits, onAddPattern, onEditPattern, onDeletePattern, onSeeStash, onAsk, firstRun }: {
  patterns: Pattern[]; fits: PatternFit[];
  onAddPattern: () => void; onEditPattern: (p: Pattern) => void; onDeletePattern: (p: Pattern) => void;
  onSeeStash: () => void; onAsk: () => void; firstRun: boolean;
}) {
  const queued = patterns.filter((p) => p.status !== "done");
  const donePatterns = patterns.filter((p) => p.status === "done");
  return (
    <section className="sk-section">
      <div className="sk-section-head">
        <h2 className="sk-section-title">Cast-on queue</h2>
        <div className="row" style={{ gap: "0.5rem" }}>
          <span className="sk-section-meta row" style={{ gap: "0.25rem" }}><ListChecks size={12} /> {queued.length} active</span>
          <button className="btn btn-sm btn-pill" onClick={onAddPattern}><Plus size={14} /> Add pattern</button>
        </div>
      </div>

      {/* On a first run the cast-on surface above already carries this exact instruction AND a
          working door, so this card said "Queue a pattern / Add pattern" for the second time on
          one screen. It stays for a user who has emptied a real queue. */}
      {queued.length === 0 ? (firstRun ? null : (
        <div className="card" style={{ padding: "2rem 1.5rem", textAlign: "center" }}>
          <div className="serif" style={{ fontSize: "1.1rem", marginBottom: "0.25rem" }}>Queue a pattern</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Add a make with its yarn-weight + total yardage. Skein checks it against your stash and tells you if you can cast on.</div>
          <button className="btn btn-primary" style={{ marginTop: "1rem" }} onClick={onAddPattern}>Add pattern</button>
        </div>
      )) : (
        <div className="sk-bandlist">
          {queued.map((p) => <PatternTile key={p.id} p={p} fit={fits.find((f) => f.pattern === p.name)}
            onEdit={() => onEditPattern(p)} onDelete={() => onDeletePattern(p)} onSeeStash={onSeeStash} />)}
        </div>
      )}

      {/* 🔴 THE ASSISTANT ENTRY — a yarn ball on the queue rail, replacing the floating "Chat"
          pill. Rendered whether or not the queue is empty, so the AI is always reachable from
          home. onAsk opens the same sheet: moved, never removed. */}
      <div className="sk-bandlist"><AskBall onAsk={onAsk} /></div>

      {donePatterns.length > 0 && (
        <>
          <h3 className="muted" style={{ fontSize: "var(--fs-sm)", margin: "1.25rem 0 0.5rem" }}>Done patterns</h3>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
            {donePatterns.map((p) => <PatternTile key={p.id} p={p} onEdit={() => onEditPattern(p)} onDelete={() => onDeletePattern(p)} />)}
          </div>
        </>
      )}
    </section>
  );
}

// ── FINISHED PROJECTS — a separate life-cycle stage, split out of the old Queue tab. ──────────
function FinishedProjectsSection({ projects, onAddProject, onEditProject, onDeleteProject }: {
  projects: Project[]; onAddProject: () => void; onEditProject: (p: Project) => void; onDeleteProject: (p: Project) => void;
}) {
  return (
    <section className="sk-section">
      <div className="sk-section-head">
        <h2 className="sk-section-title">Finished projects</h2>
        <button className="btn btn-sm btn-pill" onClick={onAddProject}><Plus size={14} /> Log finish</button>
      </div>
      {projects.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0" }}>
          Log a finished make + the yarn it consumed — it sharpens the recap’s averages.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {projects.map((p) => <ProjectTile key={p.id} p={p} onEdit={() => onEditProject(p)} onDelete={() => onDeleteProject(p)} />)}
        </div>
      )}
    </section>
  );
}

function statusChip(status: string) {
  const map: Record<string, string> = { queued: "Queued", making: "Making", done: "Done" };
  return map[status] ?? status;
}
function PatternTile({ p, fit, onEdit, onDelete, onSeeStash }: { p: Pattern; fit?: PatternFit; onEdit: () => void; onDelete: () => void; onSeeStash?: () => void }) {
  return (
    // 🔴 IDENTITY (2026-08-30): a SKEIN BAND — the printed paper label round a hank — not a
    // rounded card. See .sk-band in styles.css.
    <div className="sk-band" onClick={onEdit}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: "0.45rem" }}>
          <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{p.name || "Untitled pattern"}</span>
          <span className="chip" style={{ padding: "0.05rem 0.4rem", fontSize: 10 }}>{statusChip(p.status)}</span>
        </div>
        <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", marginTop: 4 }}>
          {[p.yarn_weight ? weightLabel(p.yarn_weight) : "", p.total_yardage ? `${fmtYds(p.total_yardage)} yds` : "", p.needle_size].filter(Boolean).join(" · ")}
        </div>
        {p.source && <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>{p.source}</div>}
        {/* The verdict belongs ON the row too, not only in the band — tapping "Queue ›" to check
            what you can make and then having to do the arithmetic yourself is the gap every other
            stash app leaves. `unknown` (no yardage on the pattern) stays silent rather than
            guessing. */}
        {fit && fit.fit !== "unknown" && (
          fit.fit === "covered" ? (
            <div className="sk-fit ok">✓ covered — {fmtYds(fit.stash_yardage_same_weight)} yds in stash</div>
          ) : (
            // The shortfall is reachable FROM the card — tap to jump to the stash it's short against.
            <button type="button" className="sk-fit short" title="See your stash"
              onClick={(ev) => { ev.stopPropagation(); onSeeStash?.(); }}>
              {fit.skeins_short ?? "?"} skein{fit.skeins_short === 1 ? "" : "s"} short — {fmtYds(Math.abs(fit.delta))} yds to go{onSeeStash ? " · see stash ↓" : ""}
            </button>
          )
        )}
      </div>
      <button className="btn" title="Delete" aria-label="Delete pattern"
        style={{ padding: "0.3rem 0.4rem", border: "1px solid transparent", background: "none", color: "var(--ink-3)" }}
        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
        <Trash2 size={15} />
      </button>
    </div>
  );
}
function ProjectTile({ p, onEdit, onDelete }: { p: Project; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="card" style={{ padding: "0.75rem 1rem", display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }} onClick={onEdit}>
      <CheckCircle2 size={18} style={{ color: "var(--fit-covered)", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{p.name || "Untitled"}</div>
        <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>
          {[p.yardage_used ? `${fmtYds(p.yardage_used)} yds used` : "", p.yarn_weight ? weightLabel(p.yarn_weight) : "", p.finished_on ? fmtDate(p.finished_on) : ""].filter(Boolean).join(" · ")}
        </div>
      </div>
      <button className="btn" title="Delete" aria-label="Delete project"
        style={{ padding: "0.3rem 0.4rem", border: "1px solid transparent", background: "none", color: "var(--ink-3)" }}
        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
        <Trash2 size={15} />
      </button>
    </div>
  );
}

// ── RECAP tab — the ONE AI feature (deterministic math + observational narration) ──
type RecapErr = { code: BrainErrorCode | "empty"; message: string };

function RecapTab({ skeinsCount, patternsCount }: { skeinsCount: number; patternsCount: number }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<StashSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      // Compute the deterministic signals locally first (fast, on-device), then send only those.
      const sig = await store.computeSignals(todayLocal());
      const r = await recapStash(sig);
      const recap = r.recap.trim();
      if (!recap) {
        setErr({ code: "empty", message: "Add a bit more to your stash to get a fuller recap." });
      } else {
        setText(recap);
        setSignals(sig);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (skeinsCount === 0) {
    return (
      <div className="card" style={{ padding: "2.5rem 1.5rem", textAlign: "center" }}>
        <BarChart3 size={34} style={{ color: "var(--accent)" }} />
        <div className="serif" style={{ fontSize: "1.2rem", margin: "0.5rem 0 0.25rem" }}>Nothing to recap yet</div>
        <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Add some yarn (and queue a pattern) — then the recap does the yardage math.</div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Stash recap
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy} onClick={run}>
          {busy ? "Reading…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Recap my stash"}
        </button>
      </div>

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
          Sums your usable yardage by weight and checks each queued pattern against your stash — surplus or shortfall, in skeins.
          {patternsCount === 0 && " Queue a pattern for the yardage-vs-pattern math."}
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gap: 8 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Doing the yardage math across your stash…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
        </div>
      )}

      {err && !busy && <RecapError err={err} onRetry={run} />}

      {text && !busy && (
        <div style={{ marginTop: "0.6rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0 }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <SignalEvidence signals={signals} />}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10 }}>Describes your own logged stash — not buying advice or resale value.</span>
          </div>
        </div>
      )}

      <AskFence />
    </div>
  );
}

// AskFence — the REAL pre-LLM refusal fence, with a live surface. Anything advice- or
// valuation-shaped ("what should I buy?", "is X yarn good?", "what's my stash worth?")
// is REFUSED locally via assertAllowed BEFORE any network/billed call (isRefusedQuery in
// brain.ts). Observational, in-scope questions point back to the deterministic recap (v1
// keeps ONE billed action — the recap — so we don't open a second billed endpoint here).
function AskFence() {
  const [q, setQ] = useState("");
  const [result, setResult] = useState<{ kind: "refused" | "ok"; message: string } | null>(null);
  function ask() {
    const query = q.trim();
    if (!query) return;
    try {
      assertAllowed(query); // throws BrainError("refused") on advice/valuation — no call made
      setResult({ kind: "ok", message: "That’s an observation about your own stash — use “Recap my stash” above; it does the yardage math and describes what you own." });
    } catch (e) {
      if (e instanceof BrainError && e.code === "refused") setResult({ kind: "refused", message: e.message });
      else setResult({ kind: "ok", message: "Use “Recap my stash” above for an observational read of what you own." });
    }
  }
  return (
    <div style={{ marginTop: "0.9rem", borderTop: "1px solid var(--border-subtle)", paddingTop: "0.8rem" }}>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 6 }}>Ask about your stash</div>
      <form className="row" style={{ gap: "0.4rem" }} onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input value={q} onChange={(e) => { setQ(e.target.value); setResult(null); }} placeholder="e.g. how much fingering do I have?" style={{ fontSize: "var(--fs-xs)" }} />
        <button type="submit" className="btn" style={{ fontSize: "var(--fs-xs)", flexShrink: 0 }} disabled={!q.trim()}>Check</button>
      </form>
      {result && (
        <div className="row" style={{ marginTop: 8, gap: "0.5rem", alignItems: "flex-start", background: result.kind === "refused" ? "var(--elev-2)" : "var(--accent-soft)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.55rem 0.7rem" }}>
          {result.kind === "refused" ? <Lock size={14} style={{ color: "var(--ink-3)", flexShrink: 0, marginTop: 1 }} /> : <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />}
          <div style={{ fontSize: "var(--fs-xs)", color: result.kind === "refused" ? "var(--ink-2)" : "var(--accent-soft-ink)" }}>{result.message}</div>
        </div>
      )}
    </div>
  );
}

function RecapError({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  if (err.code === "refused") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Lock size={15} style={{ color: "var(--ink-3)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(217,147,58,0.1)", border: "1px solid rgba(217,147,58,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep recapping</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then recap again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the deterministic figures the recap drew from. Purely observational —
// usable yardage by weight, pattern fit (covered/short + skeins-short), dye-lot risk —
// so the user sees the math the AI narrated. No advice, no valuation.
/**
 * THE CAST-ON SURFACE — Skein's answer to "what can I actually make?", as the TOP of the app on
 * its own surface (not the shared `.hero` DOM the fleet siblings all sit in). Reconciles the queue
 * against the stash using the already-computed `pattern_fit` (covered = enough yardage in that
 * weight to start today; short = how many skeins to go). A yardage total tells a knitter what they
 * own; this tells them what they can DO with it — the thing category leaders leave to the
 * knitter's own arithmetic. The one billed recap is folded on here as a knitting-language action,
 * not a peer tab; the warehouse total has moved down to the stash section.
 */
function CastOnSurface({ signals, queued, onAddPattern, recapOpen, onToggleRecap, onSeeStash, firstRun, onSaved }: {
  signals: StashSignals | null; queued: number;
  onAddPattern: () => void; recapOpen: boolean; onToggleRecap: () => void; onSeeStash: () => void;
  firstRun: boolean; onSaved: () => Promise<void> | void;
}) {
  const fits = (signals?.pattern_fit ?? []).filter((f) => f.fit !== "unknown");
  const covered = fits.filter((f) => f.fit === "covered");
  const short = fits.filter((f) => f.fit === "short");
  // Closest near-miss first — "2 skeins short" is a shopping list; "40 skeins short" is noise.
  const nearest = [...short].sort((a, b) => (a.skeins_short ?? 99) - (b.skeins_short ?? 99))[0];

  return (
    <section className="sk-caston">
      <div className="sk-caston-lbl"><Layers size={13} /> Ready to cast on</div>
      <div className="serif sk-caston-head">
        {firstRun ? "Can I cast this on?" : fits.length === 0
          ? (queued === 0 ? "Queue a pattern" : "Add yardage to a pattern")
          : covered.length > 0
            ? `${covered.length} pattern${covered.length === 1 ? "" : "s"} covered`
            : "Nothing covered yet"}
      </div>
      <div className="sk-caston-sub">
        {firstRun
          ? "A verdict needs two things: what the pattern takes, in its weight — and yarn you own in that same weight. Put one of each below and Skein answers."
          : fits.length === 0
          ? (queued === 0
              ? "Queue one with its yardage and Skein will tell you if your stash covers it."
              : "Add the yardage a queued pattern needs and Skein will check it against your stash.")
          : covered.length > 0
            ? "Enough in your stash, in the right weight, to start today."
            : nearest
              ? `Closest is “${nearest.pattern}” — ${nearest.skeins_short ?? "?"} skein${nearest.skeins_short === 1 ? "" : "s"} short.`
              : "Your queue needs weights your stash doesn't cover."}
      </div>
      {nearest && covered.length === 0 && (
        <button type="button" className="sk-caston-nearest" onClick={onSeeStash}>See your stash ↓</button>
      )}
      {/* READY-STATE (2026-09-19): with nothing queued and nothing in the stash there is no
          verdict to show, and the surface could only describe one. The door below IS the
          verdict's shape — a pattern's weight and yardage on one side, the yarn you have on the
          other — so a new knitter reads what a cast-on verdict is by filling it in, and gets a
          real one in the same breath. */}
      {firstRun && <CastOnDoor onSaved={onSaved} onFullForm={onAddPattern} />}

      <div className="sk-caston-actions">
        {/* The door's own "or fill the full pattern card" link already reaches this on a first
            run; two primary buttons on one surface would compete. */}
        {!firstRun && <button className="btn btn-primary btn-pill" onClick={onAddPattern}><Plus size={15} /> Add pattern</button>}
        <button className="btn btn-pill" onClick={onToggleRecap}>
          <Sparkles size={15} /> {recapOpen ? "Hide stash read" : "Read my whole stash back"}
        </button>
      </div>
    </section>
  );
}

// ── THE DOOR — a cast-on verdict from nothing, in one block ──────────────────────
// A verdict needs two objects: a queued pattern (weight + yardage) and stash in that weight.
// The full editors ask for brand / colorway / fiber / grams / dye-lot / shop / price / photo and
// needle size / source / notes — right for a stash you are cataloguing, wrong for the first
// minute. These six fields create one pattern and one stash line and land a real verdict; the
// layout is deliberately the verdict's own arithmetic, so filling it in explains it.
function CastOnDoor({ onSaved, onFullForm }: { onSaved: () => Promise<void> | void; onFullForm: () => void }) {
  const [pattern, setPattern] = useState("");
  const [needs, setNeeds] = useState("");
  const [weight, setWeight] = useState("dk");
  const [yarn, setYarn] = useState("");
  const [have, setHave] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const needsN = Number(needs.trim());
  const haveN = Number(have.trim());
  const ready = pattern.trim() !== "" && Number.isFinite(needsN) && needsN > 0
    && yarn.trim() !== "" && Number.isFinite(haveN) && haveN > 0;

  async function save() {
    if (!ready || saving) return;
    setSaving(true); setErr(null);
    try {
      await store.createPattern({ name: pattern.trim(), yarn_weight: weight, total_yardage: Math.round(needsN), status: "queued" });
      await store.createSkein({ brand: yarn.trim(), weight, yardage_per_skein: Math.round(haveN), skein_count: 1 });
      setPattern(""); setNeeds(""); setYarn(""); setHave("");
      await onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sk-door">
      <div className="sk-door-line">
        <span className="sk-door-key">The make</span>
        <input className="sk-door-name" placeholder="Pattern name" value={pattern}
          onChange={(e) => { setPattern(e.target.value); setErr(null); }} aria-label="Pattern name" />
        <input className="sk-door-num tnum" placeholder="yds" inputMode="numeric" value={needs}
          onChange={(e) => setNeeds(e.target.value.replace(/[^\d]/g, ""))} aria-label="Yardage the pattern needs" />
      </div>

      <div className="sk-door-line">
        <span className="sk-door-key">In</span>
        <select className="sk-door-weight" value={weight} onChange={(e) => setWeight(e.target.value)} aria-label="Yarn weight">
          {YARN_WEIGHTS.map((w) => <option key={w} value={w}>{weightLabel(w)}</option>)}
        </select>
      </div>

      <div className="sk-door-line">
        <span className="sk-door-key">You have</span>
        <input className="sk-door-name" placeholder="Yarn brand" value={yarn}
          onChange={(e) => setYarn(e.target.value)} aria-label="Yarn you have" />
        <input className="sk-door-num tnum" placeholder="yds" inputMode="numeric" value={have}
          onChange={(e) => setHave(e.target.value.replace(/[^\d]/g, ""))} aria-label="Yardage you have" />
      </div>

      <div className="sk-door-hint">
        Skein compares them in the same weight and answers covered, or how many skeins short —
        on this device, free, whatever else you add later.
      </div>
      {err && <div className="sk-door-err">{err}</div>}

      <button className="btn btn-primary sk-door-go" disabled={!ready || saving} onClick={save}>
        {saving ? "Checking…" : "Check my stash"}
      </button>
      <button className="link sk-door-full" onClick={onFullForm}>
        or fill the full pattern card — needles, source, notes
      </button>
    </div>
  );
}

function SignalEvidence({ signals }: { signals: StashSignals }) {
  const fits = signals.pattern_fit.filter((f) => f.fit !== "unknown");
  const topWeights = signals.by_weight.filter((w) => w.usable_yardage > 0).slice(0, 4);
  const maxYds = Math.max(1, ...topWeights.map((w) => w.usable_yardage));

  return (
    <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.7rem" }}>
      {/* Weight mix bars */}
      {topWeights.length > 0 && (
        <div>
          <div className="muted" style={{ fontSize: 10, marginBottom: 4 }}>Usable yardage by weight</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 5 }}>
            {topWeights.map((w) => (
              <div key={w.weight}>
                <div className="row tnum" style={{ justifyContent: "space-between", fontSize: 10, marginBottom: 2 }}>
                  <span>{weightLabel(w.weight)}</span>
                  <span className="muted">{fmtYds(w.usable_yardage)} yds</span>
                </div>
                <div className="mix-bar"><div className="mix-fill" style={{ width: `${(w.usable_yardage / maxYds) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pattern fit rows */}
      {fits.length > 0 && (
        <div>
          <div className="muted" style={{ fontSize: 10, marginBottom: 4 }}>Queued pattern fit</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 4 }}>
            {fits.slice(0, 5).map((f) => (
              <div key={f.pattern} className="row tnum" style={{ justifyContent: "space-between", fontSize: 11, gap: "0.4rem" }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{f.pattern}</span>
                {f.fit === "covered" ? (
                  <span className="fit-pill fit-covered"><CheckCircle2 size={11} /> +{fmtYds(f.delta)} yds</span>
                ) : (
                  <span className="fit-pill fit-short">
                    <AlertTriangle size={11} /> {fmtYds(Math.abs(f.delta))} yds short
                    {f.skeins_short ? ` · ~${f.skeins_short} skein${f.skeins_short === 1 ? "" : "s"}` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dye-lot alerts */}
      {signals.dye_lot_alerts.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
          {signals.dye_lot_alerts.slice(0, 4).map((d) => (
            <span key={`${d.brand}-${d.colorway}`} className="chip" title={`${d.distinct_lots} dye lots: ${d.lots.join(", ")}`}
              style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
              <AlertTriangle size={10} /> {d.colorway || d.brand}: {d.distinct_lots} lots
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Editors ────────────────────────────────────────────────────────────────────
// A <label> forwards any click on its CAPTION to the first labelable element inside it — and
// <button> IS labelable. So a Field wrapping a segmented button group turned a tap on its own
// caption into a phantom press of the FIRST chip, silently rewriting the record with no visible
// cause. Measured live on this app 2026-08-28 at 375: tapping "Yarn weight" set the yarn to
// "Lace (0)" in all three editors, and tapping "Status" in a pattern's EDIT form reset a
// pattern that was "Making" back to "Queued" — which then persisted on save. Yarn weight is the
// load-bearing field here (the stash↔queue fit math buckets by weight), so a phantom weight
// makes the cast-on verdict wrong, not just cosmetically off.
// Fleet fix (Round Card → Hivewise → Skein): keep the <label> for real inputs, where caption-tap
// focusing is the point, and render `group` fields as a <div role="group"> that has nothing to
// forward to.
function Field({ label, children, hint, group }: { label: string; children: React.ReactNode; hint?: string; group?: boolean }) {
  const inner = (
    <>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-xs)", marginBottom: 4 }}>{label}{hint && <span className="muted" style={{ fontWeight: 400 }}> · {hint}</span>}</div>
      {children}
    </>
  );
  const css: React.CSSProperties = { display: "block", marginBottom: "0.8rem" };
  return group
    ? <div role="group" aria-label={label} style={css}>{inner}</div>
    : <label style={css}>{inner}</label>;
}
function numOrUndef(v: string): number | undefined {
  if (v.trim() === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function SkeinEditor({ editing, onClose, onSaved }: { editing: Skein | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [brand, setBrand] = useState(editing?.brand ?? "");
  const [colorway, setColorway] = useState(editing?.colorway ?? "");
  const [fiber, setFiber] = useState(editing?.fiber ?? "");
  const [weight, setWeight] = useState(editing?.weight ?? "");
  const [yds, setYds] = useState(editing?.yardage_per_skein ? String(editing.yardage_per_skein) : "");
  const [grams, setGrams] = useState(editing?.grams_per_skein ? String(editing.grams_per_skein) : "");
  const [count, setCount] = useState(editing?.skein_count != null ? String(editing.skein_count) : "1");
  const [gramsLeft, setGramsLeft] = useState(editing?.grams_remaining != null ? String(editing.grams_remaining) : "");
  const [dyeLot, setDyeLot] = useState(editing?.dye_lot ?? "");
  const [boughtWhere, setBoughtWhere] = useState(editing?.bought_where ?? "");
  const [boughtOn, setBoughtOn] = useState(editing?.bought_on ?? "");
  const [price, setPrice] = useState(editing?.price ? String(editing.price) : "");
  const [currency, setCurrency] = useState(editing?.currency ?? "USD");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!brand.trim()) { setErr("Brand is required."); return; }
    setSaving(true); setErr(null);
    const payload: NewSkein = {
      brand: brand.trim(), colorway: colorway.trim(), fiber: fiber.trim(), weight,
      yardage_per_skein: numOrUndef(yds), grams_per_skein: numOrUndef(grams),
      skein_count: numOrUndef(count) ?? 1, grams_remaining: numOrUndef(gramsLeft),
      dye_lot: dyeLot.trim(), bought_where: boughtWhere.trim(), bought_on: boughtOn.trim(),
      price: numOrUndef(price), currency: currency.trim(), notes: notes.trim(),
    };
    try {
      if (isEditing) await store.updateSkein(editing!.id, payload);
      else await store.createSkein(payload);
      onSaved();
    } catch (e) {
      setErr(`Could not save — ${e instanceof Error ? e.message : String(e)}`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit yarn" : "Add yarn"} onCancel={onClose} wide>
      <Field label="Brand"><input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Brooklyn Tweed" autoFocus /></Field>
      <Field label="Colorway"><input value={colorway} onChange={(e) => setColorway(e.target.value)} placeholder="e.g. Hawthorne" /></Field>

      <Field label="Fiber" hint="type or pick">
        <input value={fiber} onChange={(e) => setFiber(e.target.value)} placeholder="e.g. merino wool" list="fibers" />
        <datalist id="fibers">{COMMON_FIBERS.map((f) => <option key={f} value={f} />)}</datalist>
      </Field>

      <Field label="Yarn weight" group>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {YARN_WEIGHTS.map((w) => (
            <button key={w} type="button" className={`seg${weight === w ? " selected" : ""}`} onClick={() => setWeight(weight === w ? "" : w)} style={{ minWidth: 0, flex: "1 0 28%" }}>{weightLabel(w)}</button>
          ))}
        </div>
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}><Field label="Yards / skein"><input inputMode="decimal" value={yds} onChange={(e) => setYds(e.target.value)} placeholder="400" /></Field></div>
        <div style={{ flex: 1 }}><Field label="Grams / skein"><input inputMode="decimal" value={grams} onChange={(e) => setGrams(e.target.value)} placeholder="100" /></Field></div>
      </div>
      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}><Field label="Skeins" hint="1.5 ok"><input inputMode="decimal" value={count} onChange={(e) => setCount(e.target.value)} placeholder="1" /></Field></div>
        <div style={{ flex: 1 }}><Field label="Grams left" hint="partial use"><input inputMode="decimal" value={gramsLeft} onChange={(e) => setGramsLeft(e.target.value)} placeholder="leave blank if full" /></Field></div>
      </div>

      <Field label="Dye lot" hint="the moat — mismatched lots stripe"><input value={dyeLot} onChange={(e) => setDyeLot(e.target.value)} placeholder="e.g. 47A" /></Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}><Field label="Bought where"><input value={boughtWhere} onChange={(e) => setBoughtWhere(e.target.value)} placeholder="shop / site" /></Field></div>
        <div style={{ flex: 1 }}><Field label="Bought on"><input type="date" value={boughtOn} onChange={(e) => setBoughtOn(e.target.value)} /></Field></div>
      </div>
      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 2 }}><Field label="Price" hint="what you paid"><input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" /></Field></div>
        <div style={{ flex: 1 }}>
          <Field label="Currency">
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {COMMON_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <Field label="Notes" hint="optional"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="project plans, substitutions…" /></Field>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !brand.trim()}>{saving ? "Saving…" : isEditing ? "Update" : "Save yarn"}</button>
      </div>
    </ModalShell>
  );
}

function PatternEditor({ editing, onClose, onSaved }: { editing: Pattern | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [weight, setWeight] = useState(editing?.yarn_weight ?? "");
  const [yds, setYds] = useState(editing?.total_yardage ? String(editing.total_yardage) : "");
  const [needle, setNeedle] = useState(editing?.needle_size ?? "");
  const [status, setStatus] = useState<PatternStatus>(editing?.status ?? "queued");
  const [source, setSource] = useState(editing?.source ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setErr("Pattern name is required."); return; }
    setSaving(true); setErr(null);
    const payload: NewPattern = {
      name: name.trim(), yarn_weight: weight, total_yardage: numOrUndef(yds),
      needle_size: needle.trim(), status, source: source.trim(), notes: notes.trim(),
    };
    try {
      if (isEditing) await store.updatePattern(editing!.id, payload);
      else await store.createPattern(payload);
      onSaved();
    } catch (e) {
      setErr(`Could not save — ${e instanceof Error ? e.message : String(e)}`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit pattern" : "Add pattern"} onCancel={onClose} wide>
      <Field label="Pattern name"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Hawthorne cardigan" autoFocus /></Field>

      <Field label="Yarn weight it calls for" group>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {YARN_WEIGHTS.map((w) => (
            <button key={w} type="button" className={`seg${weight === w ? " selected" : ""}`} onClick={() => setWeight(weight === w ? "" : w)} style={{ minWidth: 0, flex: "1 0 28%" }}>{weightLabel(w)}</button>
          ))}
        </div>
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}><Field label="Total yardage" hint="what it needs"><input inputMode="decimal" value={yds} onChange={(e) => setYds(e.target.value)} placeholder="1400" /></Field></div>
        <div style={{ flex: 1 }}><Field label="Needle size"><input value={needle} onChange={(e) => setNeedle(e.target.value)} placeholder="US 6 / 4.0mm" /></Field></div>
      </div>

      <Field label="Status" group>
        <div className="row" style={{ gap: "0.4rem" }}>
          {(["queued", "making", "done"] as PatternStatus[]).map((st) => (
            <button key={st} type="button" className={`seg${status === st ? " selected" : ""}`} onClick={() => setStatus(st)} style={{ flex: 1 }}>{statusChip(st)}</button>
          ))}
        </div>
      </Field>

      <Field label="Source" hint="optional"><input value={source} onChange={(e) => setSource(e.target.value)} placeholder="designer / book / site" /></Field>
      <Field label="Notes" hint="optional"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="size, mods…" /></Field>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !name.trim()}>{saving ? "Saving…" : isEditing ? "Update" : "Add to queue"}</button>
      </div>
    </ModalShell>
  );
}

function ProjectEditor({ editing, onClose, onSaved }: { editing: Project | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [weight, setWeight] = useState(editing?.yarn_weight ?? "");
  const [used, setUsed] = useState(editing?.yardage_used ? String(editing.yardage_used) : "");
  const [finishedOn, setFinishedOn] = useState(editing?.finished_on ?? todayLocal());
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setErr("Project name is required."); return; }
    setSaving(true); setErr(null);
    const payload: NewProject = {
      name: name.trim(), yarn_weight: weight, yardage_used: numOrUndef(used),
      finished_on: finishedOn.trim(), notes: notes.trim(),
    };
    try {
      if (isEditing) await store.updateProject(editing!.id, payload);
      else await store.createProject(payload);
      onSaved();
    } catch (e) {
      setErr(`Could not save — ${e instanceof Error ? e.message : String(e)}`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit project" : "Log finished project"} onCancel={onClose} wide>
      <Field label="Project name"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Striped beanie" autoFocus /></Field>
      <Field label="Yarn weight" group>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {YARN_WEIGHTS.map((w) => (
            <button key={w} type="button" className={`seg${weight === w ? " selected" : ""}`} onClick={() => setWeight(weight === w ? "" : w)} style={{ minWidth: 0, flex: "1 0 28%" }}>{weightLabel(w)}</button>
          ))}
        </div>
      </Field>
      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}><Field label="Yardage used"><input inputMode="decimal" value={used} onChange={(e) => setUsed(e.target.value)} placeholder="320" /></Field></div>
        <div style={{ flex: 1 }}><Field label="Finished on"><input type="date" value={finishedOn} onChange={(e) => setFinishedOn(e.target.value)} /></Field></div>
      </div>
      <Field label="Notes" hint="optional"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="how it went…" /></Field>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !name.trim()}>{saving ? "Saving…" : isEditing ? "Update" : "Log it"}</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(43,29,34,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 460 : 340, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
